import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  Settings, 
  Bell, 
  Shield, 
  Mail, 
  Eye,
  Trash2,
  Save
} from 'lucide-react';

export default function DashboardSettings() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState({
    emailUpdates: true,
    eventReminders: true,
    newsAlerts: false,
    memberMessages: true
  });
  const [privacy, setPrivacy] = useState({
    showInDirectory: true,
    showEmail: false,
    showPhone: false
  });
  const [loading, setLoading] = useState(false);

  const handleSaveNotifications = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Notification preferences saved!');
    setLoading(false);
  };

  const handleSavePrivacy = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Privacy settings saved!');
    setLoading(false);
  };

  return (
    <div className="space-y-8" data-testid="dashboard-settings">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-600">Manage your account preferences and privacy settings</p>
      </div>

      {/* Notification Settings */}
      <Card className="border border-slate-200 shadow-lg">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#3D4F7A]" />
            Notification Preferences
          </CardTitle>
          <CardDescription>
            Choose how you want to be notified about AREA updates
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Email Updates</Label>
              <p className="text-sm text-slate-500">Receive weekly digest of AREA news and updates</p>
            </div>
            <Switch 
              checked={notifications.emailUpdates}
              onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, emailUpdates: checked }))}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Event Reminders</Label>
              <p className="text-sm text-slate-500">Get notified about upcoming events you've registered for</p>
            </div>
            <Switch 
              checked={notifications.eventReminders}
              onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, eventReminders: checked }))}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">News Alerts</Label>
              <p className="text-sm text-slate-500">Receive breaking news and important announcements</p>
            </div>
            <Switch 
              checked={notifications.newsAlerts}
              onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, newsAlerts: checked }))}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Member Messages</Label>
              <p className="text-sm text-slate-500">Get notified when other members contact you</p>
            </div>
            <Switch 
              checked={notifications.memberMessages}
              onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, memberMessages: checked }))}
            />
          </div>
          <div className="pt-4">
            <Button 
              onClick={handleSaveNotifications}
              className="bg-[#3D4F7A] hover:bg-[#2A3755] text-white"
              disabled={loading}
            >
              <Save className="w-4 h-4 mr-2" />
              Save Preferences
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Privacy Settings */}
      <Card className="border border-slate-200 shadow-lg">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Eye className="w-5 h-5 text-[#3D4F7A]" />
            Privacy Settings
          </CardTitle>
          <CardDescription>
            Control what information is visible to other members
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Show in Member Directory</Label>
              <p className="text-sm text-slate-500">Allow other members to find you in the directory</p>
            </div>
            <Switch 
              checked={privacy.showInDirectory}
              onCheckedChange={(checked) => setPrivacy(prev => ({ ...prev, showInDirectory: checked }))}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Display Email Address</Label>
              <p className="text-sm text-slate-500">Show your email to other members in the directory</p>
            </div>
            <Switch 
              checked={privacy.showEmail}
              onCheckedChange={(checked) => setPrivacy(prev => ({ ...prev, showEmail: checked }))}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Display Phone Number</Label>
              <p className="text-sm text-slate-500">Show your phone number to other members</p>
            </div>
            <Switch 
              checked={privacy.showPhone}
              onCheckedChange={(checked) => setPrivacy(prev => ({ ...prev, showPhone: checked }))}
            />
          </div>
          <div className="pt-4">
            <Button 
              onClick={handleSavePrivacy}
              className="bg-[#3D4F7A] hover:bg-[#2A3755] text-white"
              disabled={loading}
            >
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Security */}
      <Card className="border border-slate-200 shadow-lg">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#3D4F7A]" />
            Account Security
          </CardTitle>
          <CardDescription>
            Manage your password and security settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input 
                id="current-password" 
                type="password" 
                placeholder="Enter current password"
                className="h-12"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input 
                  id="new-password" 
                  type="password" 
                  placeholder="Enter new password"
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input 
                  id="confirm-password" 
                  type="password" 
                  placeholder="Confirm new password"
                  className="h-12"
                />
              </div>
            </div>
          </div>
          <Button className="bg-[#3D4F7A] hover:bg-[#2A3755] text-white">
            Update Password
          </Button>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border border-red-200 shadow-lg">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            Danger Zone
          </CardTitle>
          <CardDescription>
            Irreversible actions for your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">Delete Account</p>
              <p className="text-sm text-slate-500">
                Permanently delete your account and all associated data
              </p>
            </div>
            <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
              Delete Account
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
